import React from "react";
import { NavigateFunction } from "react-router-dom";
import { IconTrash } from "@tabler/icons-react";
import { Button, Badge, Typography } from "@altimateai/lego";
import { EvalResult } from "../../services/evals";
import { ExpandedResultRow } from "./ExpandedResultRow";
import { formatDateTime } from "../utils";

interface ResultsTableProps {
  results: EvalResult[];
  expandedResultId: string | null;
  setExpandedResultId: (id: string | null) => void;
  deleteResultMutation: {
    mutate: (id: string) => void;
  };
  navigate: NavigateFunction;
}

export const ResultsTable: React.FC<ResultsTableProps> = ({
  results,
  expandedResultId,
  setExpandedResultId,
  deleteResultMutation,
  navigate,
}) => {
  return (
    <div className="al-w-full al-overflow-hidden al-border al-border-gray-200 al-rounded-md">
      <table className="al-w-full al-divide-y al-divide-gray-200 al-table-fixed">
        <colgroup>
          <col className="al-w-[25%]" />
          <col className="al-w-[20%]" />
          <col className="al-w-[10%]" />
          <col className="al-w-[10%]" />
          <col className="al-w-[10%]" />
          <col className="al-w-[15%]" />
          <col className="al-w-[10%]" />
        </colgroup>
        <thead className="al-bg-gray-50">
          <tr>
            <th className="al-px-4 al-py-3 al-text-left">
              <Typography size="xs" weight="regular" className="al-text-gray-500">
                Test Name
              </Typography>
            </th>
            <th className="al-px-4 al-py-3 al-text-left">
              <Typography size="xs" weight="regular" className="al-text-gray-500">
                Tags
              </Typography>
            </th>
            <th className="al-px-4 al-py-3 al-text-left">
              <Typography size="xs" weight="regular" className="al-text-gray-500">
                Score
              </Typography>
            </th>
            <th className="al-px-4 al-py-3 al-text-left">
              <Typography size="xs" weight="regular" className="al-text-gray-500">
                Status
              </Typography>
            </th>
            <th className="al-px-4 al-py-3 al-text-left">
              <Typography size="xs" weight="regular" className="al-text-gray-500">
                Turns
              </Typography>
            </th>
            <th className="al-px-4 al-py-3 al-text-left">
              <Typography size="xs" weight="regular" className="al-text-gray-500">
                Executed At
              </Typography>
            </th>
            <th className="al-px-4 al-py-3 al-text-left">
              <Typography size="xs" weight="regular" className="al-text-gray-500">
                Actions
              </Typography>
            </th>
          </tr>
        </thead>
        <tbody className="al-bg-white al-divide-y al-divide-gray-200">
          {results.map(result => {
            const isExpanded = expandedResultId === result.id;
            return (
              <React.Fragment key={result.id}>
                <tr
                  className={`hover:al-bg-gray-50 al-cursor-pointer ${isExpanded ? "al-bg-blue-50" : ""}`}
                  onClick={() => {
                    const newExpandedId = isExpanded ? null : result.id;
                    setExpandedResultId(newExpandedId);
                    if (newExpandedId) {
                      navigate(`?tab=results&result_id=${result.id}`, { replace: true });
                    } else {
                      navigate(`?tab=results`, { replace: true });
                    }
                  }}
                >
                  <td className="al-px-4 al-py-3 al-text-[13px] al-text-gray-900 al-font-medium">
                    <div className="al-flex al-flex-row al-items-center al-gap-2">
                      <span className="al-text-gray-400 al-flex-shrink-0">
                        {isExpanded ? "▼" : "▶"}
                      </span>
                      <span className="al-break-words al-min-w-0">{result.test_case_name}</span>
                    </div>
                  </td>
                  <td className="al-px-4 al-py-3 al-text-[13px] al-text-gray-600 al-align-top al-overflow-hidden">
                    <div className="al-flex al-flex-row al-flex-wrap al-gap-1 al-break-words">
                      {result.test_case_tags &&
                        result.test_case_tags.length > 0 &&
                        result.test_case_tags.map(tag => (
                          <Badge
                            key={tag}
                            className="al-text-[11px] al-px-2 al-py-1 !al-bg-cyan-100 !al-text-cyan-700 al-border al-border-cyan-200 al-whitespace-nowrap al-flex-shrink-0"
                          >
                            {tag}
                          </Badge>
                        ))}
                      {!result.test_case_tags?.length && (
                        <span className="al-text-gray-400">-</span>
                      )}
                    </div>
                  </td>
                  <td className="al-px-4 al-py-3 al-text-[13px]">
                    <Badge
                      className={
                        Math.round(result.overall_score) >= 4
                          ? "!al-bg-green-100 !al-text-green-700 al-border al-border-green-200"
                          : Math.round(result.overall_score) >= 3
                            ? "!al-bg-yellow-100 !al-text-yellow-700 al-border al-border-yellow-200"
                            : "!al-bg-red-100 !al-text-red-700 al-border al-border-red-200"
                      }
                    >
                      {Math.round(result.overall_score)}/5
                    </Badge>
                  </td>
                  <td className="al-px-4 al-py-3 al-text-[13px]">
                    {result.overall_score >= 4 ? (
                      <Badge className="!al-bg-green-100 !al-text-green-700 al-border al-border-green-200">
                        Pass
                      </Badge>
                    ) : result.overall_score >= 3 ? (
                      <Badge className="!al-bg-yellow-100 !al-text-yellow-700 al-border al-border-yellow-200">
                        Ok
                      </Badge>
                    ) : (
                      <Badge className="!al-bg-red-100 !al-text-red-700 al-border al-border-red-200">
                        Fail
                      </Badge>
                    )}
                  </td>
                  <td className="al-px-4 al-py-3 al-text-[13px] al-text-gray-600">
                    {result.turn_results?.length ?? 0}
                  </td>
                  <td className="al-px-4 al-py-3 al-text-[13px] al-text-gray-600">
                    {formatDateTime(result.created_at)}
                  </td>
                  <td className="al-px-4 al-py-3 al-text-[13px]" onClick={e => e.stopPropagation()}>
                    <Button
                      onClick={() => {
                        if (window.confirm(`Are you sure you want to delete this result?`)) {
                          deleteResultMutation.mutate(result.id);
                        }
                      }}
                      variant="outline"
                      size="sm"
                      className="al-text-red-600 hover:al-text-red-700 hover:al-bg-red-50 al-border-red-200"
                    >
                      <IconTrash className="al-w-4 al-h-4" />
                    </Button>
                  </td>
                </tr>
                {isExpanded && <ExpandedResultRow result={result} />}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
